import { foldForSearch, type AssignedInquiry, type InquiryStatus } from '@ghp/domain'

import type { CachedInquiry, OutboxState } from '../../../offline/data-db'
import { returnedToField } from '../../../offline/outbox'

/**
 * Le tri et les filtres de « Mes contrôles » — hors du composant pour être vérifiables sans
 * monter d'écran, et parce que l'ordre d'une tournée est une règle métier : l'équipe se
 * déplace rue par rue.
 *
 * **« Tous les types » et « Tous les clients » sont revenus le 22/09/2026.** Ils manquaient
 * parce que ni le type ni le client n'existaient dans `AssignedInquiry` ; la tournée les
 * descend depuis que le modèle les porte (`Inquiry.purpose`, `Inquiry.client_id`).
 *
 * ⚠️ **Une colonne ne revient qu'en payant sa place.** Mesuré au navigateur sur les vrais
 * fichiers de style : à 1024 px, largeur d'une tablette courante, « N° » et « Type » poussaient
 * **« RDV » et « État » hors de l'écran**. L'agent perdait l'heure de son rendez-vous et l'état
 * de son contrôle. C'est une contrainte de largeur, pas un manque de colonne en base : la
 * remplir ne la lève pas. « Type » est revenue avec #674 parce que l'adresse a pris une seule
 * colonne sur deux lignes (`my-inspections.module.css`) ; « N° » reste dehors. Ne rien remettre
 * sans remesurer.
 */

/**
 * Les deux vues de « Mes contrôles » : ce qu'il reste à faire, et ce qui est parti.
 *
 * Une vue, pas un filtre — d'où sa place hors de {@link ListFilters}. Un filtre restreint une
 * liste que l'agent voit déjà ; ici les deux ensembles ne se mélangent jamais, et « à faire »
 * est le seul qui réponde à la question qu'il se pose devant sa tablette.
 */
export const LIST_SCOPES = ['todo', 'transmitted'] as const

export type ListScope = (typeof LIST_SCOPES)[number]

/**
 * La vue active, **dans l'URL** et non dans un `useState` d'écran.
 *
 * Ouvrir une fiche démonte « Mes contrôles » — l'éditeur est une route sœur, pas un panneau. Un
 * état de composant repartirait donc sur « À faire » à chaque retour, et la ligne que l'agent
 * vient de quitter ne serait nulle part sur l'écran d'arrivée : trois consultations de contrôles
 * transmis, trois allers-retours à refaire à la main. La vue est **porteuse** — une action la
 * désigne —, là où un filtre ne l'est pas ; c'est ce qui la met dans la route (même raisonnement
 * que l'onglet de « Équipes » au back-office). Elle survit du même coup à un rechargement, ce
 * qu'une tablette fait pour de vrai (D22).
 */
export interface ListScopeSearch {
  view: ListScope
}

/** La valeur par défaut, celle que `stripSearchParams` retire de l'URL : `/` reste `/`. */
export const LIST_SCOPE_DEFAULTS: ListScopeSearch = { view: 'todo' }

/**
 * Lit la vue d'une URL **sans jamais refuser d'entrer** (M21) : un `?view=envoyes` venu d'un
 * signet écrit avant un renommage retombe sur « À faire » plutôt que de laisser l'agent devant
 * un écran d'erreur au milieu de sa tournée.
 */
export function validateListScopeSearch(search: Record<string, unknown>): ListScopeSearch {
  const view = search.view
  return {
    view: LIST_SCOPES.includes(view as ListScope) ? (view as ListScope) : LIST_SCOPE_DEFAULTS.view,
  }
}

/** Ce qu'une ligne de tournée porte de l'état de sa transmission. */
export interface TransmissionRow {
  inquiry: CachedInquiry
  /** L'état du dernier envoi de ce contrôle, `null` tant que rien n'a été transmis. */
  sync: OutboxState | null
  /** Quand ce dernier envoi a été **accepté** par le serveur, `null` s'il ne l'est pas encore. */
  sentAt: string | null
  /**
   * L'instant du dernier avis de passage laissé **depuis cette tablette** (Terrain 2.8).
   * Facultatif : seuls « Mes contrôles » et sa pré-fiche le lisent.
   */
  passageNoticedAt?: string | null
}

/**
 * Les états de `Inquiry.status` (M3) qui disent que **le bureau est désormais propriétaire** du
 * contrôle.
 */
const HANDED_OVER: readonly TourState[] = ['submitted', 'unattached', 'validated', 'archived']

/**
 * Le contrôle est-il passé au bureau ? Lu par la vue « Transmis » **et** par la pré-fiche
 * d'ouverture, qui n'offre alors que la consultation : deux lectures de la même règle, pour que
 * la liste ne puisse pas ranger une ligne dans « Transmis » pendant que la fenêtre qu'elle ouvre
 * propose encore de saisir.
 */
export function isHandedOver(state: TourState): boolean {
  return HANDED_OVER.includes(state)
}

/**
 * Une transmission **attend en file** : la fiche est verrouillée et partira (ou vient d'être
 * refusée), sans être encore au bureau. La ligne reste dans « À faire » (M21), mais il n'y a plus
 * de passage à constater ni de saisie à ouvrir — la pré-fiche la traite comme transmise.
 *
 * ⚠️ Un refus ou un échec ne vaut « en file » que **tant que la fiche est verrouillée** (`locked`) :
 * une fois rouverte, l'entrée reste la dernière de la file jusqu'au prochain envoi, mais l'agent
 * est revenu à la saisie — souvent sur place, pour refaire les prises que le refus réclamait, ou
 * alléger un schéma trop lourd (#246).
 */
export function hasQueuedHandOver(sync: OutboxState | null, locked: boolean): boolean {
  if (sync === 'rejected' || sync === 'failed') return locked
  return sync !== null && sync !== 'sent'
}

/**
 * Où en est un contrôle **pour l'agent qui le lit sur sa tablette** : les sept états du cycle de
 * vie (M3), plus deux que le cycle ne nomme pas — le renvoi, qu'il ramène en `in_progress`, et
 * l'attente de créneau, qu'il range sous `assigned`.
 *
 * ⚠️ Ce n'est pas `Inquiry.status`, et c'est tout l'objet de #181 : l'API ne sert à la tournée
 * que `assigned` et `in_progress` (`FIELD_VISIBLE_STATUSES`), donc le statut en cache dit encore
 * « Planifié » sur un contrôle que le bureau a reçu. Afficher ce statut tel quel faisait dire à
 * la colonne ÉTAT le contraire de la colonne SYNCHRO, sur la même ligne.
 *
 * ⚠️ **`unscheduled` n'est pas `received`**, bien que les deux se lisent « À planifier » : les
 * valeurs de ce type qui portent le nom d'un statut en gardent le sens — `HANDED_OVER` et
 * {@link statesOf} s'y fient —, et une demande affectée à l'équipe n'est pas une demande que le
 * bureau n'a affectée à personne. Rendre `received` ici serait un contresens que le jour où
 * `FIELD_VISIBLE_STATUSES` s'élargira rendrait visible.
 */
export type TourState =
  | InquiryStatus
  | 'returned'
  | 'unscheduled'
  | 'passage_notice'
  // Un contrôle libre transmis (#419, M41) : reçu, mais qu'aucune demande ne porte encore.
  | 'unattached'

/**
 * L'état d'un contrôle dans la tournée, **des quatre faits que la tablette possède**, dans cet
 * ordre :
 *
 * 1. **Le statut descendu fait foi dès qu'il dit que le bureau tient le contrôle.** Voie encore
 *    inatteignable — l'API ne sert pas ces états-là —, gardée pour le jour où la tournée servie
 *    les couvrira : c'est elle qui sortira la ligne sur la tablette du binôme, et c'est aussi
 *    elle qui empêchera alors une descente postérieure au dépôt de passer pour un renvoi.
 * 2. ⚠️ **Un dépôt accepté ne vaut que jusqu'au renvoi au terrain.** `submitted → in_progress`
 *    est une marche du cycle de vie à part entière — le bureau rend à l'équipe un contrôle à
 *    corriger. Rien dans la PWA ne supprime ni ne réinitialise une entrée d'envoi : sans ce
 *    garde-fou, le `sent` de la veille classerait la ligne « transmis » **à vie**, et l'agent ne
 *    verrait jamais le contrôle qu'on lui demande de reprendre.
 * 3. ⚠️ **Affecter une équipe et poser un créneau sont deux gestes indépendants du bureau**, et
 *    un seul statut les porte : `received → assigned` pose l'équipe (M4) sans écrire d'
 *    `Appointment`, et `scheduledAt` reste `null`. La ligne annonçait donc « Planifié » pendant
 *    que l'onglet Infos de la même fiche écrivait « Aucun rendez-vous posé » — deux phrases
 *    contradictoires sur un même contrôle. Le prototype validé sépare bien les deux états
 *    (`aPlanifier` et `planifie`), et ne pose le second qu'au moment où une date est écrite.
 * 4. À défaut, le statut en cache — la demande n'a pas bougé côté bureau.
 *
 * ⚠️ **`sent` et lui seul**, parce que `sent` est le seul état de file qui dise que le *serveur*
 * a accepté. `pending`, `sending`, `failed` et `rejected` laissent le dossier exactement où il
 * était : c'est la colonne SYNCHRO qui porte cette attente, pas l'état du contrôle.
 */
export function tourStateOf(row: TransmissionRow): TourState {
  if (HANDED_OVER.includes(row.inquiry.status)) return row.inquiry.status
  if (row.sync === 'sent') {
    if (returnedFrom(row)) return 'returned'

    return row.inquiry.freeInspection === undefined ? 'submitted' : 'unattached'
  }
  // ⚠️ `assigned` seul : un contrôle `in_progress` est ouvert, l'agent y est — lui redire
  // « à planifier » le renverrait attendre un rendez-vous qu'il est en train d'honorer. Et
  // `?? null` parce qu'une tablette dont le cache précède ce champ le relit `undefined`.
  if (row.inquiry.status === 'assigned' && (row.inquiry.scheduledAt ?? null) === null) {
    return 'unscheduled'
  }
  // ⚠️ **L'avis vaut jusqu'au rendez-vous suivant** : le bureau qui repose un créneau après le
  // passage manqué rend le contrôle « Planifié », sans que la tablette ait rien à effacer.
  if (noticedSinceAppointment(row)) return 'passage_notice'

  return row.inquiry.status
}

function noticedSinceAppointment({ inquiry, passageNoticedAt }: TransmissionRow): boolean {
  if (passageNoticedAt == null) return false
  const scheduledAt = inquiry.scheduledAt ?? null

  return scheduledAt === null || Date.parse(passageNoticedAt) > Date.parse(scheduledAt)
}

/**
 * La preuve du renvoi, lue sur les deux horodatages de la ligne — **définie une seule fois**,
 * dans l'outbox (`offline/outbox.ts`), parce que la reprise de la fiche en dépend autant que
 * cet écran : c'est le même fait qui autorise le déverrouillage (#228) et qui fait afficher
 * « Renvoyé ». Deux lectures séparées, c'était une fiche annonçant un renvoi qu'elle refuse
 * de laisser reprendre.
 */
function returnedFrom(row: TransmissionRow): boolean {
  return returnedToField({ servedAt: row.inquiry.servedAt, sentAt: row.sentAt })
}

/**
 * Le contrôle a-t-il quitté le travail de l'équipe ?
 *
 * ⚠️ **Un envoi encore en jeu passe avant tout le reste** : `pending`, `sending`, `failed` et
 * `rejected` retiennent la ligne dans « à faire », quoi que dise le serveur. Un relevé refusé
 * rangé dans « transmis » serait un relevé que plus personne ne regarde — et le 409 de dépôt
 * veut précisément dire que rien n'est enregistré (M21 : signaler, jamais escamoter).
 *
 * Le reste est déjà dit par {@link tourStateOf} : la vue et la colonne ÉTAT se lisent ainsi dans
 * la **même** règle, et ne peuvent plus se contredire sur une ligne.
 */
function isTransmitted(row: TransmissionRow): boolean {
  if (row.sync !== null && row.sync !== 'sent') return false

  return isHandedOver(tourStateOf(row))
}

/**
 * Le contrôle est-il encore **du travail de l'équipe** ? Non dès qu'il est passé au bureau, **ou**
 * qu'une transmission attend en file : l'équipe n'a plus de rendez-vous à honorer, et la pré-fiche
 * ne l'ouvrirait qu'en consultation. Un contrôle **renvoyé** le redevient.
 *
 * ⚠️ Ne retire plus rien de l'agenda (#577, qui revient sur #408) : un contrôle fait y reste,
 * grisé. La règle ne sert plus qu'à dire si la tablette tient une tournée, quand la période
 * affichée est vide — un contrôle transmis n'en est pas une.
 *
 * ⚠️ Plus large que la vue « Transmis » de « Mes contrôles » : une transmission en file y reste
 * dans « À faire », pour que sa pastille de synchro se voie (M21).
 */
export function isTeamWork(row: TransmissionRow, locked: boolean): boolean {
  if (hasQueuedHandOver(row.sync, locked)) return false

  return !isHandedOver(tourStateOf(row))
}

/**
 * La tournée coupée en deux vues, **en une passe**. Rien n'est supprimé : les deux partages
 * lisent le même cache.
 *
 * Une partition plutôt que deux filtres : cet appel se rejoue à chaque écriture de la file
 * d'envoi et à chaque montée de média, et il rend structurellement impossible un désaccord
 * entre le compteur d'un onglet et la liste qu'il ouvre.
 */
export function partitionScopes<T extends TransmissionRow>(
  rows: readonly T[],
): Record<ListScope, T[]> {
  const partition: Record<ListScope, T[]> = { todo: [], transmitted: [] }

  for (const row of rows) partition[isTransmitted(row) ? 'transmitted' : 'todo'].push(row)

  return partition
}

/** Les quatre tris du prototype, dans son ordre. */
export const LIST_SORTS = ['street', 'city', 'reference', 'state'] as const

export type ListSort = (typeof LIST_SORTS)[number]

export interface ListFilters {
  /**
   * La recherche libre de la barre de titre, `''` quand l'agent n'a rien tapé.
   *
   * Un filtre comme les autres, rangé avec eux bien que le champ vive ailleurs à l'écran : le
   * compteur « n sur m », « Réinitialiser » et le passage d'une vue à l'autre doivent la
   * traiter comme une ville — trois endroits qui l'oublieraient laisseraient l'agent devant une
   * liste tronquée sans rien à l'écran pour l'expliquer.
   */
  search: string
  /** `null` = « Tous les états ». L'état **affiché**, jamais le statut en cache. */
  state: TourState | null
  /** `null` = « Toutes les villes ». */
  city: string | null
  /**
   * `null` = « Tous les types ». Le **code** du type, pas son libellé : c'est ce que la ligne
   * porte, et un libellé changerait de valeur le jour où GHP renomme « Vente » (M2, M13).
   */
  purpose: string | null
  /** `null` = « Tous les clients ». Le **nom**, la tablette ne descendant pas d'identifiant. */
  clientName: string | null
  /**
   * `null` = « Secteur : tous » (#580). L'**identifiant** du lot, pas son code : le code n'est
   * unique que chez son client (`lotSchema`, M1).
   */
  lot: string | null
  sort: ListSort
}

export const NO_FILTERS: ListFilters = {
  search: '',
  state: null,
  city: null,
  purpose: null,
  clientName: null,
  lot: null,
  sort: 'street',
}

/** Un filtre est-il posé ? C'est ce qui décide d'offrir « Réinitialiser ». */
export function hasActiveFilters(filters: ListFilters): boolean {
  return (
    filters.search.trim() !== '' ||
    filters.state !== null ||
    filters.city !== null ||
    filters.purpose !== null ||
    filters.clientName !== null ||
    filters.lot !== null ||
    filters.sort !== NO_FILTERS.sort
  )
}

/**
 * Les villes réellement présentes dans la vue, dédoublonnées et classées.
 *
 * Dérivées des données et non d'une liste fermée : la tournée d'une équipe couvre quelques
 * communes, et proposer les 44 000 du référentiel ferait du filtre un annuaire. Les
 * demandes sans commune (M21 — une adresse incomplète s'affiche quand même) n'y entrent
 * pas : elles resteront visibles en « Toutes les villes ».
 *
 * ⚠️ **`active` entre toujours, même si plus aucune ligne ne la porte.** La vue bouge sous les
 * pieds de l'agent — une passe de synchro fait basculer les envois de Cholet en « transmis »
 * pendant qu'il filtre sur Cholet dans « à faire ». Sans ce rattrapage, le menu ne contient
 * plus la valeur qu'il affiche : la liste est vide, et rien à l'écran ne permet ni de
 * comprendre pourquoi, ni de revenir en arrière autrement qu'en réinitialisant tout.
 */
export function citiesOf(inquiries: readonly AssignedInquiry[], active?: string | null): string[] {
  const cities = new Set<string>()
  for (const inquiry of inquiries) {
    const city = inquiry.address.city?.trim()
    if (city !== undefined && city !== '') cities.add(city)
  }
  if (active !== undefined && active !== null) cities.add(active)

  return [...cities].sort((a, b) => collator.compare(a, b))
}

/**
 * Les états présents dans la vue — même raison que pour les villes, `active` compris.
 *
 * ⚠️ Dérivés de {@link tourStateOf}, jamais de `inquiry.status` : le menu doit proposer ce que
 * la colonne ÉTAT affiche. Offrir « Planifié » devant une liste de lignes marquées « Transmis »
 * rendrait une liste vide au premier choix, sans qu'aucun écran n'en donne la raison.
 */
export function statesOf(rows: readonly TransmissionRow[], active?: TourState | null): TourState[] {
  const states = new Set<TourState>()
  for (const row of rows) states.add(tourStateOf(row))
  if (active !== undefined && active !== null) states.add(active)
  return [...states]
}

/**
 * Les types de contrôle présents dans la vue — même parti que les villes, `active` compris :
 * dérivés des données, jamais d'une liste fermée. Une équipe ne fait pas les six types.
 */
export function purposesOf(
  inquiries: readonly AssignedInquiry[],
  active?: string | null,
): string[] {
  const purposes = new Set<string>()
  for (const inquiry of inquiries) {
    const purpose = inquiry.purpose?.trim()
    if (purpose !== undefined && purpose !== '') purposes.add(purpose)
  }
  if (active !== undefined && active !== null) purposes.add(active)

  return [...purposes].sort((a, b) => collator.compare(a, b))
}

/** Les donneurs d'ordre présents dans la vue — même parti, `active` compris. */
export function clientsOf(inquiries: readonly AssignedInquiry[], active?: string | null): string[] {
  const clients = new Set<string>()
  for (const inquiry of inquiries) {
    const name = inquiry.clientName?.trim()
    if (name !== undefined && name !== '') clients.add(name)
  }
  if (active !== undefined && active !== null) clients.add(active)

  return [...clients].sort((a, b) => collator.compare(a, b))
}

/** Le secteur d'un contrôle tel que la tournée le descend (#580). */
export type TourLot = NonNullable<AssignedInquiry['lot']>

/**
 * Les secteurs présents dans la vue, une fois chacun et classés par code — même parti que les
 * villes, `active` compris (#580). Ils viennent de la tournée et non d'une liste de référence :
 * la tablette n'a pas les lots hors ligne.
 *
 * ⚠️ `active` est le **secteur**, pas son identifiant : son libellé se compose de son nom, que seule une
 * ligne de la tournée porte.
 */
export function lotsOf(inquiries: readonly AssignedInquiry[], active?: TourLot | null): TourLot[] {
  const lots = new Map<string, TourLot>()
  for (const inquiry of inquiries) {
    // `?? null` : une ligne en cache d'avant ce champ le relit `undefined` (D22).
    const lot = inquiry.lot ?? null
    if (lot !== null) lots.set(lot.id, lot)
  }
  if (active !== undefined && active !== null) lots.set(active.id, active)

  return [...lots.values()].sort((a, b) => collator.compare(a.code, b.code))
}

/** « Secteur CQF-2026-01 — Campagne systématique — Bourg » : le libellé du bureau (#552). */
export function lotLabel(lot: TourLot): string {
  return `Secteur ${lot.code} — ${lot.name}`
}

/**
 * Comparateur français : `localeCompare` par défaut classerait « Élise » après « Zoé », et
 * une rue sur deux du secteur porte un accent. Instancié une fois — le construire par
 * comparaison coûte plus que la comparaison elle-même.
 */
const collator = new Intl.Collator('fr', { sensitivity: 'base', numeric: true })

/**
 * ⚠️ `numeric: true` fait aussi le travail sur les rues : « Rue du 8 Mai » se classe avant
 * « Rue du 11 Novembre », là où une comparaison de chaînes mettrait « 11 » avant « 8 ».
 */
function compareBy(sort: ListSort, a: TransmissionRow, b: TransmissionRow): number {
  switch (sort) {
    case 'street':
      return compareStreets(a.inquiry.address.street ?? '', b.inquiry.address.street ?? '')
    case 'city': {
      // ⚠️ La commune **puis la rue**, jamais la commune seule : une tournée en compte deux ou
      // trois, donc ce tri laisserait sinon des paquets de vingt lignes dans l'ordre où le cache
      // les a reçues — un ordre qui change à chaque descente. Trier par ville sert à faire la
      // commune d'un bout à l'autre ; dedans, l'équipe se déplace toujours rue par rue.
      const byCity = collator.compare(a.inquiry.address.city ?? '', b.inquiry.address.city ?? '')

      return byCity === 0 ? compareBy('street', a, b) : byCity
    }
    case 'reference':
      return collator.compare(a.inquiry.reference ?? '', b.inquiry.reference ?? '')
    case 'state':
      return TOUR_STATE_RANK[tourStateOf(a)] - TOUR_STATE_RANK[tourStateOf(b)]
  }
}

/**
 * Rue par rue, **puis** numéro par numéro — comme le prototype, qui trie sur la voie.
 *
 * ⚠️ La ligne reçue porte le numéro en tête (« 8 bis rue de la Paix », recomposée par l'API) :
 * la comparer telle quelle rangeait la tournée par numéro de maison, toutes rues mêlées. Le
 * numéro de tête est donc mis de côté le temps du tri — pour classer, pas pour stocker (M1).
 */
function compareStreets(a: string, b: string): number {
  const left = streetOrder(a)
  const right = streetOrder(b)

  return (
    collator.compare(left.name, right.name) ||
    left.number - right.number ||
    collator.compare(left.suffix, right.suffix)
  )
}

/** « 8 bis rue de la Paix » → la voie, le numéro, son suffixe ; sans numéro de tête, la ligne. */
function streetOrder(line: string): { name: string; number: number; suffix: string } {
  const match = /^\s*(\d+)\s*(bis|ter|quater|[a-z])?\b[\s,]*(.+)$/iu.exec(line)
  if (match === null) return { name: line, number: 0, suffix: '' }

  return { name: match[3] ?? '', number: Number(match[1]), suffix: match[2] ?? '' }
}

/**
 * L'ordre de tournée des états, et **non l'ordre alphabétique de leurs identifiants**.
 *
 * Le prototype classe ses quatre états par l'avancement du travail (`ghp-ct/app.js:105` :
 * à planifier, avis de passage, planifié, en cours) — c'est ce que l'agent veut voir remonter
 * quand il trie par état : ce qu'il lui reste à faire d'abord. Comparer les identifiants
 * rendait `assigned` avant `in_progress` par hasard d'alphabet, et `returned` — le contrôle que
 * le bureau lui redemande — perdu au milieu.
 *
 * ⚠️ Les états que le prototype ne connaît pas prennent le rang que leur place dans le cycle de
 * vie (M3) appelle : le renvoi juste après « en cours », puisque c'est du travail qui revient,
 * et ce qui est parti au bureau en queue, dans son propre ordre.
 */
const TOUR_STATE_RANK: Record<TourState, number> = {
  received: 0,
  unscheduled: 1,
  passage_notice: 2,
  assigned: 3,
  in_progress: 4,
  returned: 5,
  submitted: 6,
  unattached: 7,
  validated: 8,
  archived: 9,
  dropped: 10,
}

/**
 * Les colonnes que la recherche parcourt, recollées comme le fait le prototype — une seule
 * chaîne, un seul `includes` : « ormes nantes » trouve donc la ligne, dans cet ordre-là.
 *
 * Les colonnes du prototype : la voie (numéro compris, la ligne le porte), la commune **et son
 * bourg** (« Montjean » trouve une adresse de Mauges-sur-Loire), et le donneur d'ordre, que la
 * tournée porte depuis la pile — « podeliha » ne trouvait rien.
 */
function searchableOf(inquiry: CachedInquiry): string {
  const { street, city, delegatedName } = inquiry.address

  return foldForSearch(
    [inquiry.caseReference, inquiry.reference, street, city, delegatedName, inquiry.clientName]
      .filter((part): part is string => part !== null && part !== undefined)
      .join(' '),
  )
}

/**
 * Filtre puis classe — sans muter l'entrée : `rows` vient d'un cache TanStack Query, qu'un
 * `sort()` en place réordonnerait pour tous les autres lecteurs.
 */
export function applyFilters<T extends TransmissionRow>(
  rows: readonly T[],
  filters: ListFilters,
): T[] {
  const wanted = foldForSearch(filters.search.trim())

  return rows
    .filter((row) => {
      if (filters.state !== null && tourStateOf(row) !== filters.state) return false
      if (filters.city !== null && row.inquiry.address.city !== filters.city) return false
      // ⚠️ `?? null` sur les deux : une tournée mise en cache avant ces champs les relit
      // `undefined`, et un filtre posé ne doit pas faire disparaître ces lignes-là en silence —
      // elles n'ont simplement pas la valeur, ce que « Tous les types » montre déjà (D22).
      if (filters.purpose !== null && (row.inquiry.purpose ?? null) !== filters.purpose) {
        return false
      }
      if (filters.clientName !== null && (row.inquiry.clientName ?? null) !== filters.clientName) {
        return false
      }
      if (filters.lot !== null && (row.inquiry.lot?.id ?? null) !== filters.lot) return false
      if (wanted !== '' && !searchableOf(row.inquiry).includes(wanted)) return false
      return true
    })
    .toSorted((a, b) => compareBy(filters.sort, a, b))
}
